import { Component, h, Host, Element, Prop, Watch, JSX, Fragment } from '@stencil/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { isMagnoliaEditingMode } from '../../helper';

interface UiCmsInfinityBodyBase {
  backgroundColor: 'white' | 'primary';
}

interface Image {
  image: {
    landscape: string;
    portrait: string;
  };
  movingImage?: never;
}

interface MovingImage {
  movingImage: {
    landscape: string;
    portrait: string;
  };
  image?: never;
}

type UiCmsInfinityBody = UiCmsInfinityBodyBase & (Image | MovingImage);

@Component({
  tag: 'ui-cms-infinity',
  styleUrl: 'ui-cms-infinity.scss',
})
export class UiCmsInfinity {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsInfinityBody = {} as UiCmsInfinityBody;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  private get isMediaTypeImage(): boolean {
    return !!this.body?.image;
  }

  private refMedia: HTMLImageElement | HTMLVideoElement | HTMLPictureElement;

  private refMediaSticky: HTMLImageElement | HTMLVideoElement | HTMLPictureElement;

  private refContent: HTMLElement;

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  componentDidLoad() {
    if (!isMagnoliaEditingMode()) {
      this.initAnimation();
    }
  }

  private initAnimation(): void {
    gsap.registerPlugin(ScrollTrigger);

    const tl = gsap.timeline({
      ease: 'none',
    });

    tl.from(this.refMedia, {
      scale: 0.7,
      duration: 1,
      transformOrigin: 'bottom center',
    }).to(
      {},
      {
        duration: 1,
      }
    );

    ScrollTrigger.create({
      trigger: this.refMedia,
      start: 'top+=200 bottom',
      end: 'bottom bottom',
      animation: tl,
      scrub: 0.8,
      onEnter: this.handleMediaEnter.bind(this),
      onEnterBack: this.handleMediaEnterBack.bind(this),
      onLeave: this.handleMediaLeave.bind(this),
    });

    ScrollTrigger.create({
      trigger: this.refContent,
      start: 'top-=1 bottom',
      end: 'bottom top',
      onEnter: this.handleStickyEnter.bind(this),
      onLeaveBack: this.handleStickyLeave.bind(this),
    });
  }

  private handleMediaEnter(): void {
    this.refMedia.classList.remove('hidden');
  }

  private handleMediaEnterBack(): void {
    this.refMedia.classList.remove('hidden');
    if (!this.isMediaTypeImage) {
      this.playVideos();
    }
  }

  private handleMediaLeave(): void {
    if (!this.isMediaTypeImage) {
      this.pauseVideos();
    }
    this.refMedia.classList.add('hidden');
  }

  private handleStickyEnter(): void {
    this.refMediaSticky.classList.remove('hidden');
    this.refMediaSticky.classList.add('blur');
  }

  private handleStickyLeave(): void {
    this.refMediaSticky.classList.add('hidden');
    this.refMediaSticky.classList.remove('blur');
  }

  private playVideos(): void {
    const stickyVideo = this.refMediaSticky.firstElementChild as HTMLVideoElement;
    if (stickyVideo instanceof HTMLVideoElement && this.refMedia instanceof HTMLVideoElement) {
      stickyVideo.play();
      this.refMedia.play();
    }
  }

  private pauseVideos(): void {
    const stickyVideo = this.refMediaSticky.firstElementChild as HTMLVideoElement;
    if (stickyVideo instanceof HTMLVideoElement && this.refMedia instanceof HTMLVideoElement) {
      this.refMedia.pause();
      stickyVideo.pause();
    }
  }

  private renderImage(sticky: boolean): JSX.Element {
    const { portrait, landscape } = this.body.image!;
    return (
      <picture
        class={{ infinity__media: !sticky }}
        ref={(el) => {
          if (!sticky) this.refMedia = el;
        }}
      >
        <source srcSet={portrait} media="(orientation: portrait)" />
        <source srcSet={landscape} media="(orientation: landscape)" />
        <img alt="" src={landscape} />
      </picture>
    );
  }

  private renderMovingImage(sticky: boolean): JSX.Element {
    const { portrait, landscape } = this.body.movingImage;
    return (
      <Fragment>
        {window.innerWidth >= 768 ? (
          <video
            class={{ infinity__media: !sticky }}
            autoPlay
            loop
            muted
            ref={(el) => {
              if (!sticky) this.refMedia = el;
            }}
            // eslint-disable-next-line
            // @ts-ignore
            disablePictureInPicture
          >
            <source src={landscape.replace('.mp4', '.webm')} type="video/webm" />
            <source src={landscape} type="video/mp4" />
          </video>
        ) : (
          <picture
            class={{ infinity__media: !sticky }}
            ref={(el) => {
              if (!sticky) this.refMedia = el;
            }}
          >
            <img alt="" src={portrait} />
          </picture>
        )}
      </Fragment>
    );
  }

  private renderMedia(sticky: boolean): JSX.Element {
    return this.isMediaTypeImage ? this.renderImage(sticky) : this.renderMovingImage(sticky);
  }

  render() {
    return (
      <Host>
        {this.renderMedia(false)}
        <div
          class={{
            infinity__mediaSticky: true,
            [`infinity__mediaSticky--${this.body.backgroundColor}`]: true,
            hidden: true,
          }}
          ref={(el) => {
            this.refMediaSticky = el;
          }}
        >
          {this.renderMedia(true)}
        </div>
        <div
          class="infinity__content"
          ref={(el) => {
            this.refContent = el;
          }}
        >
          <ui-cms-container body={{ backgroundStyle: isMagnoliaEditingMode() ? 'white' : this.body.backgroundColor }}>
            <slot></slot>
          </ui-cms-container>
        </div>
      </Host>
    );
  }
}
